import { Thresholds } from "@src/types/dashboards";

export interface GaugeZone {
  from: number; // degrees, 0 = min (left), 180 = max (right)
  to: number;
  color: string;
}

// Zone colours (below warning → warning..critical → above critical).
// The drawer can later expose these as configurable fields.
export const ZONE_OK = "#19af00"; // green
export const ZONE_WARNING = "#fd7f38"; // amber/orange
export const ZONE_CRITICAL = "#ef4444"; // red

/** Map a value to a gauge angle (0..180), clamped to the min..max range. */
export const toDeg = (v: number, min: number, max: number): number => {
  const span = max - min || 1;
  return Math.min(180, Math.max(0, ((v - min) / span) * 180));
};

/**
 * Build the coloured arc segments (3-zone gauge).
 * - no thresholds           → single green arc
 * - warning only            → green up to warning, amber past it
 * - critical only           → green up to critical, red past it
 * - warning + critical      → green / amber / red
 * An inverted pair (warning > critical) is normalised so arcs never run
 * backwards; out-of-range thresholds clamp to the arc ends.
 */
export const buildGaugeZones = (
  min: number,
  max: number,
  thresholds: Thresholds | null | undefined,
  okColor: string = ZONE_OK,
  warningColor: string = ZONE_WARNING,
  criticalColor: string = ZONE_CRITICAL,
): GaugeZone[] => {
  if (!thresholds) {
    return [{ from: 0, to: 180, color: okColor }];
  }

  let warn = thresholds.warning ?? null;
  let crit = thresholds.critical ?? null;
  if (warn != null && crit != null && warn > crit) {
    [warn, crit] = [crit, warn];
  }
  const d = (v: number) => toDeg(v, min, max);

  if (warn == null && crit == null) {
    return [{ from: 0, to: 180, color: okColor }];
  }
  if (warn != null && crit != null) {
    return [
      { from: 0, to: d(warn), color: okColor },
      { from: d(warn), to: d(crit), color: warningColor },
      { from: d(crit), to: 180, color: criticalColor },
    ];
  }
  if (warn != null) {
    return [
      { from: 0, to: d(warn), color: okColor },
      { from: d(warn), to: 180, color: warningColor },
    ];
  }
  return [
    { from: 0, to: d(crit as number), color: okColor },
    { from: d(crit as number), to: 180, color: criticalColor },
  ];
};
